import { Timestamp } from "firebase/firestore";

// Enums
export enum ServiceHostname {}
// Add your service hostnames here

export enum OrganisationClaims {}
// Add your organisation claims here

export enum IndexStatus {}
// Add your index statuses here

export enum SecurityFindingEntityType {
  DOCUMENT = "document",
  CONNECTION = "connection",
  GROUP = "group",
}

export enum RecommendedAction {
  GRANT = "grant",
  REVOKE = "revoke",
  UNCHANGED = "unchanged",
}

export enum SecurityFindingSeverityLevel {
  CRITICAL = "critical",
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

export enum SecurityFindingImpactLevel {
  CRITICAL = "critical",
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

export enum SecurityFindingKind {
  ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT = "Anomalous Permissions on Sensitive Document",
  PUBLIC_DOCUMENT = "Public Document",
  LARGE_SCALE_COLLABORATION_ON_PERSONAL_DRIVE = "Large Scale Collaboration on Personal Drive",
  PERMISSIONS_MANAGED_THROUGH_SECONDARY_IDENTITY = "Permissions Managed Through Secondary Identity",
  PERSONAL_DRIVE_WITH_SENSITIVE_DOCUMENTS = "Personal Drive with Sensitive Documents",
  DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL = "Document Accessed by External Principal",
}

export enum DomainJoinPolicy {
  AUTO_JOIN_ORG = "auto_join_org",
  BLOCK_JOIN_ORG = "block_join_org",
}

export enum FetcherStatus {
  PREVIEW = "PREVIEW",
  GENERAL_AVAILABILITY = "GA",
  DEPRECATED = "DEPRECATED",
}

export enum OperationModes {
  UNKNOWN = "unknown",
  SCHEDULE = "schedule",
  DISCOVERY = "discovery",
  INGESTION = "ingestion",
  GRPC_SERVER = "grpc_server",
  SECURITY__GRAPH_ANALYSIS = "security__graph_analysis",
  SECURITY__ALGORITHMS = "security__algorithms",
  SECURITY__PREPROCESSOR = "security__preprocessor",
  SECURITY__POSTPROCESSOR = "security__postprocessor",
}

// Basic types
export interface TimeSpan {
  after?: Timestamp;
  before?: Timestamp;
}

export interface Embedding {
  modelId: string;
  vector: number[];
}

export interface ChunkReference {
  // Add properties as needed
}

export interface ChunkMetadata {
  // Add properties as needed
}

export interface RelevantChunkRelevance {
  // Add properties as needed
}

export interface SourceReference {
  // Add properties as needed
  connectionId: string;
  documentId: string;
  documentVersion: string;
  documentName: string;
  documentPath: string;
  system: string;
  systemVersion: string;
}

export interface Chunk {
  sourceRef: SourceReference;
  chunkRef: ChunkReference;
  metadata: Record<string, any>;
  body: string;
}

export interface EmbeddedChunk {
  sourceRef: SourceReference;
  chunkRef: ChunkReference;
  metadata: Record<string, any>;
  embeddings: Embedding[];
}

export interface RelevantChunkMetadata {
  sourceRef: SourceReference;
  chunkRef: ChunkReference;
  metadata: ChunkMetadata;
  relevance: RelevantChunkRelevance;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
}

export type Scope = [string, string | null, string[]]; // [ProviderId, DocumentPath, ProviderVersion[]]

export interface IngestionFilters {
  scopes: string[];
  created?: TimeSpan;
  modified?: TimeSpan;
}

export interface DatabaseFilters {
  scopes: Scope[];
  created?: TimeSpan;
  modified?: TimeSpan;
  userIds: string[];
  includeContentInTrash: boolean;
}

export interface SemanticQuery {
  embedding: Embedding;
  semanticQuery: string;
}

export interface FetchServiceInfo {
  status: FetcherStatus;
  limits: [string, any][];
}

export interface DocumentPermissions {
  source: SourceReference;
  permissions: Record<string, any>;
  sensitiveFieldNames: string[];
}

export interface SystemReference {
  system: string;
  systemVersion: string;
}

export interface ConnectionReference extends SystemReference {
  connectionId: string;
}

export interface GroupReference extends ConnectionReference {
  groupId: string;
  groupType?: "sitegroup";
}

export interface Operation {
  mode: OperationModes;
  version?: string;
  runId?: string;
}

export interface SecurityFindingReference {
  system: string;
  systemVersion: string;
  securityFindingId: string;
  securityFindingVersion: string;
}

export interface User {
  id?: string;
  organisationClaims?: OrganisationClaims[];
  teamClaims?: Record<string, string[]>;
  teamClaimsOverage?: Record<string, string[]>;
  lastLogin?: Timestamp;
  usage?: Record<string, any>;
  preferences?: {
    name?: string;
    primaryEmail?: string;
    defaultTeam?: string;
  };
}

export interface Team {
  id?: string;
  created?: Timestamp;
  alias?: string;
  organisation?: string;
  willDeleteOn?: Timestamp;
}

export interface TeamMember {
  id?: string;
  claims?: string[];
}

export interface TeamApplication {
  id?: string;
  created?: Timestamp;
  description?: string;
  redirectUris?: string[];
}

export interface TeamApplicationKey {
  id?: string;
  expiry?: Timestamp;
  creator?: string;
}

export interface MethodUsage {
  data: Record<string, number>;
  interval: string;
  unit: string;
}

export interface TeamApplicationUsage {
  updatedAt?: Timestamp;
  final?: boolean;
  queryChunks?: MethodUsage;
}

export interface OrganisationPolicy {
  blockNewUsers?: boolean;
  blockNewTeams?: boolean;
  blockNewApps?: boolean;
  block3rdPartyApps?: boolean;
}

export interface PrimaryIndex {
  isManaged?: boolean;
  serviceHostname?: ServiceHostname;
  indexId?: string;
}

export interface IndexStats {
  documentCount?: number;
  chunkCount?: number;
  indexSize?: number;
  usersCount?: number;
}

export interface IndexationSettings {
  // Add specific settings as needed
}

export interface PurchaseInfo {
  details?: Record<string, any>;
  marketplace?: string;
}

export interface Organisation {
  id?: string;
  created?: Timestamp;
  alias?: string;
  willDeleteOn?: Timestamp;
  organisationRootPolicy?: OrganisationPolicy;
  primaryIndex?: PrimaryIndex;
  indexStats?: IndexStats;
  indexationSettings?: IndexationSettings;
  purchaseInfo?: PurchaseInfo;
}

export interface ManagedIndexProvider {
  hostname?: string;
  id?: string;
  kind: "opensearch";
  metadata?: Record<string, any>;
}

export interface IndexInstance {
  id?: string;
  created?: Timestamp;
  modified?: Timestamp;
  recycled?: Timestamp;
  status?: IndexStatus;
  primarySize?: number;
  totalSize?: number;
  chunkCount?: number;
}

export enum Severity {
  INFO = "info",
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

export interface SecurityFindingRecommendation {
  anomalyScore?: number;
  principal?: string;
  email?: string;
  principalType?: string;
  recommendedAction?: string;
}

export interface SensitiveTopic {
  sensitiveTopic: string;
  sensitivityScore: number;
}

export interface SecurityFindingsStats {
  totalNumberOfFindings: number;
  misconfiguredPermissions: number;
}

export interface DocumentWithLargeScaleCollaboration {
  documentResourceId: string;
  collaborators: Collaborator[];
}

export interface Collaborator {
  principalResourceId: string;
  principalType: string;
  principalEmail: string;
}

export interface Permission {
  resourceId: string;
  principalResourceId: string;
  principalType: string;
  principalEmail: string;
  grantType: string;
  isWriter: boolean;
  isOwner: boolean;
  principalIsExternal?: boolean;
}

export interface SecurityFindingResource {
  resourceId: string;
  resourceVersion: string;
  createdAt: Timestamp;
  modifiedAt: Timestamp;
  name: string;
  path: string;
  link: string;
  isSensitive: boolean;
  sensitiveTopics: SensitiveTopic[];
  owner: string;
  ownerType: string;
  ownerEmail: string;
}

// Custom InfoType for Sensitive Data Protection
export type CustomInfoTypeDetectionType = "regex" | "dictionary";

export type CustomInfoTypeLikelihood =
  | "VERY_LIKELY"
  | "LIKELY"
  | "POSSIBLE"
  | "UNLIKELY"
  | "VERY_UNLIKELY";

export interface CustomInfoType {
  id: string;
  name: string;
  displayName: string;
  description?: string | null;
  detectionType: CustomInfoTypeDetectionType;
  regexPattern?: string | null;
  dictionaryWords?: string[] | null;
  likelihood: CustomInfoTypeLikelihood;
  enabled: boolean;
  created?: string | null;
  modified?: string | null;
}

export interface CreateCustomInfoTypeRequest {
  name: string;
  displayName: string;
  description?: string;
  detectionType: CustomInfoTypeDetectionType;
  regexPattern?: string;
  dictionaryWords?: string[];
  likelihood?: CustomInfoTypeLikelihood;
  enabled?: boolean;
}

export interface UpdateCustomInfoTypeRequest {
  displayName?: string;
  description?: string;
  regexPattern?: string;
  dictionaryWords?: string[];
  likelihood?: CustomInfoTypeLikelihood;
  enabled?: boolean;
}
